import CloseIcon from "@mui/icons-material/Close";
import { Box, IconButton, Tooltip, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import {
  formatAttachmentSize,
  getAttachmentIcon,
} from "../attachments/fileType";
import { MessageAttachment } from "../attachments/types";

type Props = {
  attachment: MessageAttachment;
  compact?: boolean;
  variant?: "plain" | "card";
  onRemove?: () => void;
  onDownload?: () => void;
};

const filenameSx = {
  flex: "1 1 auto",
  minWidth: 0,
  display: "block",
  color: "text.primary",
  lineHeight: 1.3,
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
  overflowWrap: "normal",
  wordBreak: "normal",
  hyphens: "none",
} as const;

const MessageAttachmentChip = ({
  attachment,
  compact = false,
  variant = "plain",
  onRemove,
  onDownload,
}: Props) => {
  const { t } = useTranslation("messaging");
  const Icon = getAttachmentIcon(attachment.filename);
  const iconSize = compact ? 20 : 24;
  const isCard = variant === "card";
  const shrinkToContent = Boolean(onRemove);
  const downloadable = Boolean(onDownload) && attachment.status === "ready";
  const rejected = attachment.status === "rejected";

  return (
    <Box
      component={downloadable ? "button" : "div"}
      type={downloadable ? "button" : undefined}
      onClick={downloadable ? onDownload : undefined}
      aria-label={
        downloadable
          ? `${attachment.filename}. ${t("downloadAttachment")}`
          : undefined
      }
      sx={{
        display: "flex",
        alignItems: "center",
        gap: "8px",
        minWidth: 0,
        overflow: "hidden",
        overflowWrap: "normal",
        wordBreak: "normal",
        hyphens: "none",
        width: shrinkToContent ? "fit-content" : "100%",
        maxWidth: isCard && !compact ? "70%" : "100%",
        border: 0,
        font: "inherit",
        textAlign: "left",
        color: "inherit",
        ...(downloadable && { cursor: "pointer" }),
        ...(isCard && {
          bgcolor: "#E8E8ED",
          borderRadius: "8px",
          padding: "8px 10px",
        }),
      }}
    >
      <Icon
        sx={{
          fontSize: iconSize,
          color: "text.secondary",
          flexShrink: 0,
        }}
      />
      <Box sx={{ flex: 1, minWidth: 0, overflow: "hidden" }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            flexWrap: "nowrap",
            gap: "4px",
            minWidth: 0,
            overflow: "hidden",
            whiteSpace: "nowrap",
          }}
        >
          <Tooltip title={attachment.filename} enterDelay={1500}>
            <Typography
              variant="body2"
              noWrap
              sx={{
                ...filenameSx,
                ...(downloadable && { textDecoration: "underline" }),
              }}
            >
              {attachment.filename}
            </Typography>
          </Tooltip>
          {onRemove && (
            <IconButton
              size="small"
              onClick={onRemove}
              aria-label={t("removeAttachment")}
              sx={{ p: "2px", flexShrink: 0 }}
            >
              <CloseIcon sx={{ fontSize: 16 }} />
            </IconButton>
          )}
        </Box>
        <Typography
          variant="body2"
          color="text.secondary"
          noWrap
          sx={{ fontSize: "12px", lineHeight: 1.3, display: "block" }}
        >
          {formatAttachmentSize(attachment.size)}
        </Typography>
        {rejected && (
          <Typography
            variant="body2"
            color="error"
            sx={{ fontSize: "12px", lineHeight: 1.3, display: "block" }}
          >
            {t("attachmentUploadFailed")}
          </Typography>
        )}
      </Box>
    </Box>
  );
};

export default MessageAttachmentChip;
